import React, {
  FC,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';

import { useMutation } from '@apollo/client';
import {
  Button,
  H1,
  InfoIcon,
  Modal,
  Paragraph,
  Preloader,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import QRCodeStyling from 'qr-code-styling';

import { LocalizationContext } from 'localization';
import { GENERATE_QR, IGenerateQRRequest, IGenerateQRResponse } from 'schemas';

import { DEFAULT_QR_OPTIONS } from './constants';
import s from './GenerateQRModal.module.scss';
import { IGenerateQRModalProps } from './interfaces';
import { generateQRForPrint } from './utils';

export const GenerateQRModal: FC<IGenerateQRModalProps> = ({
  hostId,
  close,
  hosts,
}) => {
  const { t, lang } = useContext(LocalizationContext);

  const [type, setType] = useState<'loading' | 'error' | 'success'>('loading');
  const [qrData, setQrData] = useState<string>('');
  const [qrCode, setQrCode] = useState<QRCodeStyling | null>(null);
  const [forceRegenerate, setForceRegenerate] = useState(false);

  const [generateQR, { loading }] = useMutation<
    IGenerateQRResponse,
    IGenerateQRRequest
  >(GENERATE_QR);

  const generateQRHandler = (): void => {
    setType('loading');
    setQrData('');
    setForceRegenerate(true);
  };

  const qrRef = useRef<HTMLDivElement>(null);
  const currentHost = hosts?.hostsOverview?.find((host) => host.id === hostId);
  const currentGroupTitle = hosts?.hostGroupsOverview?.find(
    (group) => group.id === currentHost?.group_id
  );

  const printQrHandler = useCallback(
    () =>
      qrCode?.getRawData('png').then((res) => {
        if (!res) return;
        const title = currentHost?.alias;
        const group = currentGroupTitle?.title;
        const qrLink = URL.createObjectURL(res as Blob);
        generateQRForPrint({ t, qrLink, lang, title, group });
      }),
    [qrCode, currentHost?.alias, currentGroupTitle?.title, t, lang]
  );

  useEffect(() => {
    if (!forceRegenerate && currentHost?.qr) {
      setQrData(currentHost.qr);
      setType('success');
      return;
    }

    if (forceRegenerate || !currentHost?.qr) {
      setType('loading');
      generateQR({
        variables: { input: { host_id: hostId } },
      })
        .then((res) => {
          if (!res.data?.generateQR) {
            setType('error');
            setQrData('');
            return;
          }
          setQrData(res.data.generateQR);
          setType('success');
        })
        .catch(() => {
          setType('error');
          setQrData('');
        })
        .finally(() => {
          setForceRegenerate(false);
        });
    }
  }, [forceRegenerate, currentHost?.qr, hostId, generateQR]);

  useEffect(() => {
    if (!qrRef.current) return;

    qrRef.current.innerHTML = '';

    if (qrData && type === 'success') {
      const newQrCode = new QRCodeStyling({
        ...DEFAULT_QR_OPTIONS,
        data: qrData,
      });

      setQrCode(newQrCode);
      newQrCode.append(qrRef.current);
    }

    return () => {
      if (qrRef.current) {
        qrRef.current.innerHTML = '';
      }
    };
  }, [qrData, type]);

  return (
    <Modal className={s.content} onClose={close}>
      <div className={s.header}>
        <H1 className={s.title}>
          {t(
            'modal.generate_qr.title',
            `${currentHost?.alias}`,
            `${currentGroupTitle?.title}`
          )}
        </H1>
        {type !== 'success' && (
          <Paragraph className={s.description}>
            {type === 'loading'
              ? t('modal.generate_qr.description.loading')
              : t('modal.generate_qr.description.error')}
          </Paragraph>
        )}
      </div>

      <div className={s.body}>
        <Preloader loading={type === 'loading' || loading}>
          {' '}
          {type === 'error' && <InfoIcon color="error" className={s.icon} />}
          <div
            ref={qrRef}
            className={cn(s.qr, type !== 'success' && s.qr_hidden)}
          />
        </Preloader>
      </div>

      {type !== 'loading' && (
        <div className={s.footer}>
          {type === 'error' ? (
            <>
              <Button fullWidth onClick={generateQRHandler}>
                {t('modal.generate_qr.button.generate')}
              </Button>
              <Button variant="secondary" fullWidth onClick={close}>
                {t('close')}
              </Button>
            </>
          ) : (
            <>
              <Button
                fullWidth
                iconName="PrinterIcon"
                onClick={printQrHandler}
                disabled={!qrData || type !== 'success'}
              >
                {t('modal.generate_qr.button.print')}
              </Button>
              <Button
                variant="outline"
                fullWidth
                onClick={generateQRHandler}
                color="red"
              >
                {t('modal.generate_qr.button.generate_new')}
              </Button>
            </>
          )}
        </div>
      )}
    </Modal>
  );
};
